import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Container, Content } from '../../styles/lib/grid';
import { Breadcrumb, DynamicFilter, Navbar, Topbar } from '../../components';
import { TitleContent } from '../../styles/lib/typography';
import { IconButton } from '../../styles/lib/buttons';
import {
  IDynamicFilterData,
  IDynamicFilterSelectOptions,
} from '../../interfaces';
import { useCommissioning } from '../../hooks/useCommissioning';
import { DynamicTable } from '../../components/DynamicTable/DynamicTable';
import { usePagination } from '../../hooks/usePagination';
import { toast } from 'react-toastify';
import { format, addDays, subMonths, differenceInDays, parse } from 'date-fns';
import { FaIdCard } from 'react-icons/fa';
import { ModalResponsive } from '../../components/modal';

const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd');

const today = new Date();
const DEFAULT_DATE_START = formatDate(subMonths(today, 3));
const DEFAULT_DATE_END = formatDate(today);

enum CommissionStatus {
  PAGAMENTO_EFETIVADO = 'Pagamento Efetivado',
  PAGAMENTO_PROVISIONADO = 'Pagamento Provisionado',
  NF_REVENDA_RECEBIDA = 'NF Revenda Recebida',
  A_LIBERAR = 'A Liberar',
  SINISTRO = 'Sinistro',
  DEVOLVIDO = 'Devolvido',
}

const COMMISSION_STATUS_CONFIG = {
  [CommissionStatus.PAGAMENTO_EFETIVADO]: 'check',
  [CommissionStatus.PAGAMENTO_PROVISIONADO]: 'money',
  [CommissionStatus.NF_REVENDA_RECEBIDA]: 'document',
  [CommissionStatus.A_LIBERAR]: 'times',
  [CommissionStatus.SINISTRO]: 'incident',
  [CommissionStatus.DEVOLVIDO]: 'returned',
} as const;

const getStatusIcon = (status: string): string => {
  return COMMISSION_STATUS_CONFIG[status as CommissionStatus] || 'money';
};

export const Comissionamento: React.FC = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const isAdmin = currentUser?.perfil === 'administrador';

  const [adminVendorCode, setAdminVendorCode] = useState('');
  const [adminVendorInput, setAdminVendorInput] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showTrocarTooltip, setShowTrocarTooltip] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
  const vendorInputRef = useRef<HTMLInputElement>(null);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(isAdmin);

  const [filters, setFilters] = useState<
    IDynamicFilterData & { extraType?: string }
  >({
    type: 'cnpj',
    term: '',
    dateStart: DEFAULT_DATE_START,
    dateEnd: DEFAULT_DATE_END,
    extraType: '0',
  });

  const [
    { isLoading, commissions, totalItems, vendorCodes },
    fetchCommissions,
    exportCommissions,
    fetchVendorCodes,
  ] = useCommissioning();

  const adminVendorCodeRef = useRef(adminVendorCode);
  adminVendorCodeRef.current = adminVendorCode;

  const buildVendorOverride = () =>
    isAdmin ? { vendorCodeOverride: adminVendorCodeRef.current } : {};

  const { currentPage, pageSize, handlePageChange, resetPagination } =
    usePagination({
      totalItems: totalItems || 0,
      onPageChange: (currentPage, pageSize) => {
        const filtersWithExtraType = {
          ...filters,
          type: !filters.term ? '' : filters.type,
          extraType: filters.extraType || '0',
        };
        fetchCommissions({
          ...filtersWithExtraType,
          ...buildVendorOverride(),
          extraType: filters.extraType || '0',
          currentPage,
          paginationSize: pageSize,
        });
      },
    });

  const validateDateRange = useCallback(
    (start: string, end: string): boolean => {
      if (!start || !end) return true;
      try {
        const startDate = parse(start, 'yyyy-MM-dd', new Date());
        const endDate = parse(end, 'yyyy-MM-dd', new Date());
        const diffDays = differenceInDays(endDate, startDate);
        return diffDays >= 0 && diffDays <= 89;
      } catch {
        return false;
      }
    },
    [],
  );

  const searchFilters = useCallback(
    (formData: IDynamicFilterData & { extraType?: string }) => {
      const filtersWithExtraType = {
        ...formData,
        type: !formData.term ? '' : formData.type,
        extraType: formData.extraType || '0',
        currentPage: 1,
        paginationSize: pageSize,
      };

      resetPagination();
      setFilters(filtersWithExtraType);
      fetchCommissions({ ...filtersWithExtraType, ...buildVendorOverride() });
    },
    [fetchCommissions, currentPage, pageSize, resetPagination, adminVendorCode],
  );

  const dataTreatment = useCallback((value: IDynamicFilterData) => {
    return { ...value, term: value.term.trim() };
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchVendorCodes();
      return;
    }

    const filtersWithExtraType = {
      ...filters,
      type: !filters.term ? '' : filters.type,
      extraType: filters.extraType || '0',
    };

    fetchCommissions({
      ...filtersWithExtraType,
      extraType: filters.extraType || '0',
      currentPage,
      paginationSize: pageSize,
    });
  }, []);

  const handleAdminVendorConfirm = useCallback(() => {
    const newCode = adminVendorInput.trim();

    if (!newCode && !adminVendorCode) {
      toast.warning('Selecione um vendedor da lista.');
      return;
    }

    if (!newCode) {
      setIsVendorModalOpen(false);
      return;
    }

    setAdminVendorCode(newCode);
    setIsVendorModalOpen(false);

    const filtersWithExtraType = {
      ...filters,
      type: !filters.term ? '' : filters.type,
      extraType: filters.extraType || '0',
    };

    fetchCommissions({
      ...filtersWithExtraType,
      extraType: filters.extraType || '0',
      currentPage,
      paginationSize: pageSize,
      vendorCodeOverride: newCode,
    });
  }, [
    adminVendorInput,
    adminVendorCode,
    filters,
    currentPage,
    pageSize,
    fetchCommissions,
  ]);

  const handleExport = useCallback(() => {
    exportCommissions({
      ...filters,
      ...buildVendorOverride(),
      type: !filters.term ? '' : filters.type,
      extraType: filters.extraType || '0',
      currentPage,
      paginationSize: pageSize,
    });
  }, [exportCommissions, filters, currentPage, pageSize, adminVendorCode]);

  const handleClearSearch = () => {
    setFilters({
      ...filters,
      term: '',
      dateStart: DEFAULT_DATE_START,
      dateEnd: DEFAULT_DATE_END,
      extraType: '0',
    });
  };

  const tableColumns = [
    'Nº do Pedido',
    'Razão social',
    'Nº da Nota',
    'Parcela',
    'Data da Parcela',
    'Data de Emissão da Nota de Vendas',
    'Val. Pedido',
    'Val. Comissão',
    'Status',
  ];

  const formatDateTime = (dateString: string) => {
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
  };

  const isNegativeValue = (value: string) => {
    if (!value) return false;
    return value?.includes('-');
  };

  const tableData = (commissions || []).map((commission: any) => ({
    'Nº do Pedido': commission.numeroPedido,
    'Razão social': commission.razaoSocial,
    'Nº da Nota': commission.numeroNota,
    Parcela: commission.parcela,
    'Data da Parcela': formatDateTime(commission.dataParcela),
    'Data de Emissão da Nota de Vendas': formatDateTime(commission.dataPedido),
    'Val. Pedido': (
      <p
        data-testid="text:val-pedido"
        style={{
          color: isNegativeValue(commission.valorPedido) ? '#C41E3A' : '#000',
        }}
      >
        {commission.valorPedido ? `R$ ${commission.valorPedido}` : ''}
      </p>
    ),
    'Val. Comissão': (
      <p
        style={{
          color: isNegativeValue(commission.valorPago) ? '#C41E3A' : '#000',
        }}
      >
        {commission.valorPago ? `R$ ${commission.valorPago}` : ''}
      </p>
    ),
    Status: {
      text: commission.status,
      icon: getStatusIcon(commission.status),
    },
  }));

  return (
    <Container>
      <Topbar />
      <Navbar />

      {isAdmin && (
        <ModalResponsive
          title="Selecionar código de vendedor (Obrigatório)"
          isOpenModal={isVendorModalOpen}
          handleOpenModal={setIsVendorModalOpen}
          isSmall
          blockClose={!adminVendorCode}
          closeIconSize={18}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '8px 0',
            }}
          >
            <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>
              Selecione o código do vendedor para visualizar os
              comissionamentos.
            </p>
            {adminVendorCode &&
              (() => {
                const found = vendorCodes?.find(
                  v => v.vendedor === adminVendorCode,
                );
                return (
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#475569',
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '8px 12px',
                    }}
                  >
                    Vendedor atual:{' '}
                    <strong style={{ color: '#1e293b' }}>
                      {found
                        ? `${found.vendedor} — ${found.nome}`
                        : adminVendorCode}
                    </strong>
                  </div>
                );
              })()}
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                ref={vendorInputRef}
                type="text"
                placeholder="Busque por código ou nome..."
                value={vendorSearch}
                onFocus={() => {
                  const rect =
                    vendorInputRef.current?.getBoundingClientRect() ?? null;
                  setDropdownRect(rect);
                  setShowVendorDropdown(true);
                }}
                onChange={e => {
                  setVendorSearch(e.target.value);
                  setAdminVendorInput('');
                  const rect =
                    vendorInputRef.current?.getBoundingClientRect() ?? null;
                  setDropdownRect(rect);
                  setShowVendorDropdown(true);
                }}
                onBlur={() =>
                  setTimeout(() => setShowVendorDropdown(false), 150)
                }
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
              {showVendorDropdown &&
                dropdownRect &&
                createPortal(
                  <ul
                    style={{
                      position: 'fixed',
                      top: dropdownRect.bottom,
                      left: dropdownRect.left,
                      width: dropdownRect.width,
                      maxHeight: '220px',
                      overflowY: 'auto',
                      border: '1px solid #ccc',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      background: '#fff',
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      zIndex: 9999,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    }}
                  >
                    {(vendorCodes ?? [])
                      .filter(
                        v =>
                          !vendorSearch ||
                          v.vendedor
                            .toLowerCase()
                            .includes(vendorSearch.toLowerCase()) ||
                          v.nome
                            .toLowerCase()
                            .includes(vendorSearch.toLowerCase()),
                      )
                      .map((v, index) => (
                        <li
                          key={`${v.vendedor}-${index}`}
                          onMouseDown={() => {
                            setAdminVendorInput(v.vendedor);
                            setVendorSearch(`${v.vendedor} — ${v.nome}`);
                            setShowVendorDropdown(false);
                          }}
                          style={{
                            padding: '10px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                          onMouseEnter={e =>
                            ((
                              e.currentTarget as HTMLLIElement
                            ).style.background = '#fff8f0')
                          }
                          onMouseLeave={e =>
                            ((
                              e.currentTarget as HTMLLIElement
                            ).style.background = '#fff')
                          }
                        >
                          <strong>{v.vendedor}</strong> — {v.nome}
                        </li>
                      ))}
                  </ul>,
                  document.body,
                )}
            </div>
            <IconButton
              className="update"
              onClick={handleAdminVendorConfirm}
              style={{ alignSelf: 'flex-end' }}
            >
              Confirmar
            </IconButton>
          </div>
        </ModalResponsive>
      )}

      <Content>
        <Breadcrumb paths={paths} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '32px',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <TitleContent className="no-bottom">
            Controle de comissionamento
          </TitleContent>
          {isAdmin && adminVendorCode && (
            <div
              onClick={() => {
                setAdminVendorInput('');
                setVendorSearch('');
                setIsVendorModalOpen(true);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: '#f1f5f9',
                border: '1px solid rgb(247, 148, 29)',
                borderRadius: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '13px',
                color: '#475569',
                fontWeight: 500,
                userSelect: 'none',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              <FaIdCard
                style={{
                  fontSize: '18px',
                  color: 'rgb(247, 148, 29)',
                  flexShrink: 0,
                }}
              />

              <span>
                Cod.Vendedor:{' '}
                <strong style={{ color: '#1e293b' }}>{adminVendorCode}</strong>
              </span>

              <span
                style={{
                  position: 'relative',
                  marginLeft: '4px',
                  paddingLeft: '8px',
                  borderLeft: '1px solid #cbd5e1',
                  color: 'rgb(247, 148, 29)',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
                onMouseEnter={() => setShowTrocarTooltip(true)}
                onMouseLeave={() => setShowTrocarTooltip(false)}
              >
                Trocar
                {showTrocarTooltip &&
                  (() => {
                    const found = vendorCodes?.find(
                      v => v.vendedor === adminVendorCode,
                    );
                    const label = found
                      ? `${found.vendedor} — ${found.nome}`
                      : adminVendorCode;
                    return (
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 'calc(100% + 6px)',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#1e293b',
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                          fontWeight: 'normal',
                          textTransform: 'none',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          zIndex: 10,
                        }}
                      >
                        {label}
                        <span
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            border: '5px solid transparent',
                            borderTopColor: '#1e293b',
                          }}
                        />
                      </span>
                    );
                  })()}
              </span>
            </div>
          )}
        </div>

        <DynamicFilter
          filterSelectOptions={filterSelectOptions}
          extraFilterSelectOptions={extraFilterSelectOptions}
          filters={filters}
          calendar={true}
          isLoading={isLoading}
          searchFilters={searchFilters}
          dataTreatment={dataTreatment}
          setFilters={setFilters}
          handleExport={handleExport}
          handleClearSearch={handleClearSearch}
          placeholder="Busque por uma nota"
          isComissionamento={true}
        />

        <DynamicTable
          columns={tableColumns}
          data={tableData}
          isLoading={isLoading}
          paginationSize={pageSize}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </Content>
    </Container>
  );
};

const paths = [
  {
    title: 'Meu Painel',
    link: '/painel',
  },
  {
    title: 'Controle de comissionamento',
    link: '',
  },
];

const filterSelectOptions: IDynamicFilterSelectOptions[] = [
  {
    value: 'cnpj',
    description: 'CNPJ',
    placeholder: 'Busque por um CNPJ',
  },
  {
    value: 'numeroPedido',
    description: 'Nº do Pedido',
    placeholder: 'Busque pelo número do pedido',
  },
  {
    value: 'nfe',
    description: 'Número da nota',
    placeholder: 'Busque por uma nota',
  },
];

const extraFilterSelectOptions: IDynamicFilterSelectOptions[] = [
  { value: '0', description: 'Todos' },
  { value: '1', description: 'A liberar' },
  { value: '2', description: 'Liberado' },
  { value: '4', description: 'NF Revenda Recebida' },
  { value: '3', description: 'Pagamento Provisionado' },
  { value: '5', description: 'Pagamento Efetivado' },
  { value: '6', description: 'Devolvido' },
  { value: '7', description: 'Sinistro' },
];
